import { eachDayOfInterval, isBefore, isEqual, max } from 'date-fns';

import { INITIAL_DATE } from 'components/shared/DatePicker/DayPicker/constants';
import { isEmpty, uniq } from 'ramda';
import { isDefined } from 'utils/ramda';

import { DatesRange } from './types';

type Props = {
  ranges: DatesRange[];
  type?: 'date' | 'day';
};

export const handleFilledDates = ({ ranges, type = 'date' }: Props) => {
  const filledDates = uniq(
    ranges.flatMap((range) => [range.to, range.from]).filter((date): date is Date => isDefined(date)),
  );

  const initialDate = type === 'date' ? new Date() : INITIAL_DATE;
  const lastDate = isEmpty(filledDates) ? initialDate : max(filledDates);

  const validateSingleDate = (range: DatesRange) => {
    if (!range.from) return false;
    if (!range.to) return true;
    return isEqual(range.from, range.to);
  };

  const singleDates = uniq(ranges.filter((range) => validateSingleDate(range)).map((range) => range.from));

  const rangeStartDates = uniq(
    ranges.map((range) => (!validateSingleDate(range) ? range.from : null)).filter((date) => isDefined(date)),
  );

  const rangeEndDates = uniq(
    ranges.map((range) => (!validateSingleDate(range) ? range.to : null)).filter((date) => isDefined(date)),
  );

  const rangeMiddleDates = uniq(
    ranges
      .flatMap((range) => {
        if (!range.from || !range.to) return [];
        if (isBefore(range.from, range.to)) {
          return eachDayOfInterval({ start: range.from, end: range.to }).slice(1, -1); // Exclude start and end
        }
        return [];
      })
      .filter(isDefined),
  );

  return { lastDate, singleDates, rangeStartDates, rangeEndDates, rangeMiddleDates };
};
